import { useEffect, useRef, useState } from 'react'
import cn from 'classnames'

import { InputRange } from '../../../../components/ui/input-range'

import onlyNumberChanger from '../../../../utils/only-numbers-value'

import { formatNumber } from '../../../../utils/formatters'

import Notice from '../../../../components/ui/notice'

import { floorNumber } from '../../../../utils/floor-number'

import CheckIcon from './icons/check-icon.svg'

import styles from './styles.module.scss'
import { useTranslation } from 'utils/use-translation'

export default function CalculateProfitReferral() {
  const { t } = useTranslation()
  
  const [loanValue, setLoanValue] = useState(3000)
  const [customerValue, setCustomerValue] = useState(10000)
  const [termValue, setTermValue] = useState(4)
  const [resultValue, setResultValue] = useState('')

  const [formattedLoanAmount, setFormattedLoanAmount] = useState(formatNumber(loanValue.toString()))
  const [formattedCustomerValue, setFormattedCustomerValue] = useState(formatNumber(customerValue.toString()))
  const [formattedTermValue, setFormattedTermValue] = useState(formatNumber(termValue.toString()))

  const loanAmountInputRef = useRef<HTMLInputElement>()
  const customerValueInputRef = useRef<HTMLInputElement>()
  const termValueInputRef = useRef<HTMLInputElement>()

  const loanMax = 100000
  const loanMin = 100

  const refMin = 100
  const refMax = 20000
  const apr = 0.024

  useEffect(() => {
    const result = (((loanValue * customerValue * apr) / 12) * termValue).toString()
    setResultValue(
      floorNumber(Number(result), 2)
        .toString()
        .replace(/(\d)(?=(\d\d\d)+([^\d]|$))/g, '$1 '),
    )
  }, [loanValue, termValue, customerValue])

  const handleChangeValue = (e, setValue) => {
    if (!isNaN(e)) {
      setValue(Math.round(e * 10) / 10)
      return
    }
    let value: string = e.target.value
    value = value.replace(' ', '')
    let numberValue = Number(value)

    if (isNaN(numberValue)) {
      return
    }
    numberValue = Math.round(numberValue * 10) / 10
    if (+value <= +e.target.min) numberValue = +e.target.min
    if (+value > +e.target.max) numberValue = +e.target.max
    setValue(value)
  }

  const handleChangeAmount = (event, setValue, formattedValue, setFormattedValue, inputRef) => {
    const rawValue = event.target.value.replace(/\s/g, '')

    let cursorPosition = event.target.selectionStart
    let newValue = ''

    if (event.nativeEvent.inputType === 'deleteContentBackward') {
      cursorPosition += 1
    } else if (event.nativeEvent.inputType === 'deleteContentForward') {
      cursorPosition += 1
    } else {
      cursorPosition -= 1
    }

    onlyNumberChanger(rawValue, (newRawValue) => {
      newValue = newRawValue

      const newFormattedValue = formatNumber(newValue)
      setFormattedValue(newFormattedValue)

      setTimeout(() => {
        if (inputRef.current) {
          const newCursorPosition = cursorPosition - (formattedValue.length - newFormattedValue.length)
          inputRef.current.setSelectionRange(newCursorPosition, newCursorPosition)
        }
      }, 0)
    })
    onlyNumberChanger(rawValue, () => handleChangeValue(event, setValue))
  }

  const handleChangeAmountRange = (val: number, setFormattedValue, setValue) => {
    setFormattedValue(formatNumber(val))
    setValue(val)
  }

  return (
    <div className={styles.profit}>
      <div className={styles.inputs}>
        <div className={styles.inputWrapper}>
          <label>{t('loan_amount')}</label>

          <div className={styles.inputContainer}>
            <div className={styles.rangeWrapper}>
              <InputRange
                maxValue={loanMax}
                minValue={loanMin}
                step={1}
                value={formattedLoanAmount}
                onChange={(val) => handleChangeAmountRange(val, setFormattedLoanAmount, setLoanValue)}
                mainColor={t('blue_hex')}
                secondColor={t('blue_hex')}
                className={styles.inputRange}
                isPinV2
              />
            </div>
            <output name="loanAmount" id="loanAmount">
              <input
                ref={loanAmountInputRef}
                className={cn(styles.input, styles.inputLoan)}
                value={formattedLoanAmount}
                step={1}
                max={loanMax}
                min={loanMin}
                onChange={(e) =>
                  handleChangeAmount(e, setLoanValue, formattedLoanAmount, setFormattedLoanAmount, loanAmountInputRef)
                }
              />{' '}
              USDT
            </output>
          </div>
        </div>

        <div className={styles.inputWrapper}>
          <label>{t('referrals_amount')}</label>

          <div className={styles.inputContainer}>
            <output name="referralAmount" id="referralAmount">
              <input
                className={cn(styles.input, styles.inputLoan)}
                value={formattedCustomerValue}
                step={1}
                max={refMax}
                min={refMin}
                onChange={(e) =>
                  handleChangeAmount(
                    e,
                    setCustomerValue,
                    formattedCustomerValue,
                    setFormattedCustomerValue,
                    customerValueInputRef,
                  )
                }
              />{' '}
            </output>
            <div className={styles.rangeWrapper}>
              <InputRange
                maxValue={refMax}
                minValue={refMin}
                step={1}
                value={formattedCustomerValue}
                onChange={(val) => handleChangeAmountRange(val, setFormattedCustomerValue, setCustomerValue)}
                mainColor={t('blue_hex')}
                isPinV2
                className={styles.inputRange}
                secondColor={t('blue_hex')}
              />
            </div>
          </div>
        </div>

        <div className={styles.inputWrapper}>
          <label>{t('loan_duration')}</label>

          <div className={styles.inputContainer}>
            <output name="termAmount" id="termAmount">
              <input
                className={cn(styles.input, styles.inputLoan)}
                value={formattedTermValue}
                step={1}
                max={24}
                min={2}
                onChange={(e) =>
                  handleChangeAmount(e, setTermValue, formattedTermValue, setFormattedTermValue, termValueInputRef)
                }
              />{' '}
              {t('loan_duration_months')}
            </output>
            <div className={styles.rangeWrapper}>
              <InputRange
                maxValue={24}
                minValue={2}
                step={1}
                value={formattedTermValue}
                onChange={(val) => handleChangeAmountRange(val, setFormattedTermValue, setTermValue)}
                mainColor={t('blue_hex')}
                secondColor={t('blue_hex')}
                isPinV2
                className={styles.inputRange}
              />
            </div>
          </div>
        </div>
      </div>

      <div className={styles.data}>
        <div className={styles.data__main}>
          <div className={styles.item}>
            <div className={styles.item__header}>
              <p>{t('profit_in_12_months')}</p>
            </div>
            <span>{resultValue} USDT</span>
          </div>

          <div className={styles.item}>
            <div className={styles.item__header}>
              <p>{t('apr')}</p>
              <Notice alt="apr notice" imgClassName={styles.notice}>
                {t('you_recieve_monthly')}
              </Notice>
            </div>
            <h5>2.4%</h5>
          </div>
        </div>

        <ul className={styles.data__list}>
          <li>
            <img src={CheckIcon} alt="check icon" />
            <p>{t('guaranteed_payouts')}</p>
          </li>
          <li>
            <img src={CheckIcon} alt="check icon" />
            <p>{t('referral_loan_profit')}</p>
          </li>
          <li>
            <img src={CheckIcon} alt="check icon" />
            <p>{t('no_earning_cap')}</p>
          </li>
        </ul>
      </div>
    </div>
  )
}
